import { useEffect, useRef, type MutableRefObject } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact';
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

type Props = {
  siteKey: string;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
};

const useSyncRef = <T,>(value: T): MutableRefObject<T> => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

export const Turnstile = ({ siteKey, onSuccess, onError, onExpire }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const onSuccessRef = useSyncRef(onSuccess);
  const onErrorRef = useSyncRef(onError);
  const onExpireRef = useSyncRef(onExpire);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const MAX = 100;

    const tryRender = () => {
      if (!containerRef.current || widgetIdRef.current) return;
      if (window.turnstile) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onSuccessRef.current(token),
          'error-callback': () => onErrorRef.current?.(),
          'expired-callback': () => onExpireRef.current?.(),
          theme: 'light',
          size: 'normal',
        });
        return;
      }
      if (++attempts < MAX) timer = setTimeout(tryRender, 50);
    };

    timer = setTimeout(tryRender, 0);

    return () => {
      clearTimeout(timer);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onSuccessRef, onErrorRef, onExpireRef]);

  return <div ref={containerRef} className="demo__turnstile" />;
};
